import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { dummyNotifications, dummyNotificationUserSuggestions } from "../../../data/dummyData";
import "../Home/Homepage.css";
import "./NotificationManagementPage.css";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "N/A";
  }
};

const TABS = [
  { key: "sendToUser", label: "Send to User", icon: "fa-user" },
  { key: "bulkNotification", label: "Bulk Notification", icon: "fa-users" },
  { key: "announceToAll", label: "Announce To All", icon: "fa-broadcast-tower" },
  { key: "managementNotification", label: "Notification List", icon: "fa-list" },
];

const NotificationManagementPage = () => {
  const [activeTab, setActiveTab] = useState("sendToUser");
  const [notifications, setNotifications] = useState(dummyNotifications);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationLink, setNotificationLink] = useState("");
  const [userInput, setUserInput] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  const counts = useMemo(() => ({
    total: notifications.length,
    active: notifications.filter((n) => n.isActive === true).length,
    inactive: notifications.filter((n) => n.isActive === false).length,
  }), [notifications]);

  const resetInput = () => {
    setNotificationTitle("");
    setNotificationMessage("");
    setNotificationLink("");
    setUserInput("");
    setSelectedUsers([]);
    setSuggestions([]);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    resetInput();
  };

  const handleFindUser = (input) => {
    setUserInput(input);
    if (!input || !input.trim()) {
      setSuggestions([]);
      return;
    }
    const q = input.trim().toLowerCase();
    const filtered = dummyNotificationUserSuggestions.filter(
      (u) =>
        (u.firstName || "").toLowerCase().includes(q) ||
        (u.lastName || "").toLowerCase().includes(q) ||
        (u.emailId || "").toLowerCase().includes(q)
    );
    setSuggestions(filtered);
  };

  const handleSelectUser = (user) => {
    if (activeTab === "sendToUser") {
      setSelectedUsers([user]);
    } else {
      const exists = selectedUsers.some((u) => u._id === user._id);
      if (!exists) setSelectedUsers((prev) => [...prev, user]);
    }
    setUserInput("");
    setSuggestions([]);
  };

  const removeUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((u) => u._id !== userId));
  };

  const handleSendToUser = (e) => {
    e.preventDefault();
    if (!notificationTitle.trim() || !notificationMessage.trim()) return;
    if (activeTab === "sendToUser" && selectedUsers.length === 0) return;
    if (activeTab === "bulkNotification" && selectedUsers.length === 0) return;
    const newNotif = {
      _id: "notif_" + Date.now(),
      createdAt: new Date().toISOString(),
      title: notificationTitle.trim(),
      message: notificationMessage.trim(),
      isActive: true,
    };
    setNotifications((prev) => [newNotif, ...prev]);
    resetInput();
  };

  const handleAnnounceToAll = (e) => {
    e.preventDefault();
    if (!notificationTitle.trim() || !notificationMessage.trim()) return;
    const newNotif = {
      _id: "notif_" + Date.now(),
      createdAt: new Date().toISOString(),
      title: notificationTitle.trim(),
      message: notificationMessage.trim(),
      isActive: true,
    };
    setNotifications((prev) => [newNotif, ...prev]);
    resetInput();
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this notification?")) {
      setNotifications((prev) => prev.filter((n) => n._id !== id));
    }
  };

  const handleToggleActive = (id) => {
    setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, isActive: !n.isActive } : n)));
  };

  return (
    <main className="dashboard-main dashboard-subpage notification-management-page">
      <section className="dashboard-hero notification-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Notifications</span>
          </p>
          <h1 className="dashboard-hero__title">Notification Management</h1>
          <p className="dashboard-hero__subtitle">Send and manage platform notifications</p>
        </div>
        <div className="notification-hero__icon">
          <i className="fas fa-bell" />
        </div>
      </section>

      <div className="dashboard-body">
        {notifications.length > 0 && (
          <div className="notification-stats">
            <div className="notification-stat-card">
              <span className="notification-stat-card__value">{counts.total}</span>
              <span className="notification-stat-card__label">Total Notifications</span>
            </div>
            <div className="notification-stat-card notification-stat-card--active">
              <span className="notification-stat-card__value">{counts.active}</span>
              <span className="notification-stat-card__label">Active</span>
            </div>
            <div className="notification-stat-card notification-stat-card--inactive">
              <span className="notification-stat-card__value">{counts.inactive}</span>
              <span className="notification-stat-card__label">Inactive</span>
            </div>
          </div>
        )}

        <div className="notification-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`notification-tab ${activeTab === tab.key ? "notification-tab--active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              <i className={`fas ${tab.icon}`} /> {tab.label}
            </button>
          ))}
        </div>

        <div className="notification-content">
          {(activeTab === "sendToUser" || activeTab === "bulkNotification") && (
            <section className="notification-section">
              <h2 className="notification-section__title">
                <i className={activeTab === "sendToUser" ? "fas fa-user" : "fas fa-users"} />
                {activeTab === "sendToUser" ? "Send Notification to User" : "Send Bulk Notifications"}
              </h2>
              <form className="notification-form" onSubmit={handleSendToUser}>
                <div className="notification-form__group notification-form__group--relative">
                  <label>Select User <span className="required">*</span></label>
                  <input
                    type="search"
                    placeholder="Search by name, email, or ID"
                    value={userInput}
                    onChange={(e) => handleFindUser(e.target.value)}
                    autoComplete="off"
                    disabled={activeTab === "sendToUser" && selectedUsers.length > 0}
                  />
                  {userInput && suggestions.length > 0 && (
                    <ul className="notification-suggestions">
                      {suggestions.map((user) => (
                        <li key={user._id} className="notification-suggestion-item" onClick={() => handleSelectUser(user)}>
                          <div className="notification-suggestion-name">{user.firstName} {user.lastName}</div>
                          <div className="notification-suggestion-email">{user.emailId}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {selectedUsers.length > 0 && (
                  <div className="notification-selected-users">
                    {selectedUsers.map((user) => (
                      <div key={user._id} className="notification-selected-user">
                        <div className="notification-selected-user-info">
                          <span className="notification-selected-user-name">{user.firstName} {user.lastName}</span>
                          <span className="notification-selected-user-email">{user.emailId}</span>
                        </div>
                        <button type="button" className="notification-btn-remove" onClick={() => removeUser(user._id)}><i className="fas fa-times" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="notification-form__group">
                  <label>Title <span className="required">*</span></label>
                  <input type="text" placeholder="Enter notification title" value={notificationTitle} onChange={(e) => setNotificationTitle(e.target.value)} required />
                </div>
                <div className="notification-form__group">
                  <label>Message <span className="required">*</span></label>
                  <textarea placeholder="Enter notification message" value={notificationMessage} onChange={(e) => setNotificationMessage(e.target.value)} rows={5} required />
                </div>
                <div className="notification-form__group">
                  <label>Link (optional)</label>
                  <input type="text" placeholder="Enter link URL" value={notificationLink} onChange={(e) => setNotificationLink(e.target.value)} />
                </div>
                <div className="notification-form__actions">
                  <button type="submit" className="notification-btn-send">
                    <i className="fas fa-paper-plane" /> {activeTab === "sendToUser" ? "Send Notification" : "Send Bulk Notifications"}
                  </button>
                </div>
              </form>
            </section>
          )}

          {activeTab === "announceToAll" && (
            <section className="notification-section">
              <h2 className="notification-section__title"><i className="fas fa-broadcast-tower" /> Send Notifications to All Users</h2>
              <form className="notification-form" onSubmit={handleAnnounceToAll}>
                <div className="notification-form__group">
                  <label>Title <span className="required">*</span></label>
                  <input type="text" placeholder="Enter notification title" value={notificationTitle} onChange={(e) => setNotificationTitle(e.target.value)} required />
                </div>
                <div className="notification-form__group">
                  <label>Message <span className="required">*</span></label>
                  <textarea placeholder="Enter notification message" value={notificationMessage} onChange={(e) => setNotificationMessage(e.target.value)} rows={5} required />
                </div>
                <div className="notification-form__group">
                  <label>Link (optional)</label>
                  <input type="text" placeholder="Enter link URL" value={notificationLink} onChange={(e) => setNotificationLink(e.target.value)} />
                </div>
                <div className="notification-form__actions">
                  <button type="submit" className="notification-btn-send">
                    <i className="fas fa-paper-plane" /> Send Notifications to All
                  </button>
                </div>
              </form>
            </section>
          )}

          {activeTab === "managementNotification" && (
            <section className="notification-section">
              <h2 className="notification-section__title"><i className="fas fa-list-alt" /> Notification List ({notifications.length})</h2>
              {notifications.length === 0 ? (
                <div className="notification-empty"><i className="fas fa-bell" /><p>No notifications found</p></div>
              ) : (
                <div className="notification-table-wrap">
                  <table className="notification-table">
                    <thead>
                      <tr>
                        <th>Created At</th>
                        <th>Notification Title</th>
                        <th>Message</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notifications.map((row) => (
                        <tr key={row._id}>
                          <td>{formatDate(row.createdAt)}</td>
                          <td>{row.title || "-----"}</td>
                          <td className="notification-table__msg">{row.message || "-----"}</td>
                          <td>
                            <span className={`notification-status-badge ${row.isActive ? "notification-status-badge--active" : "notification-status-badge--inactive"}`}>
                              {row.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <div className="notification-actions">
                              <button type="button" className="notification-action-btn notification-action-btn--delete" onClick={() => handleDelete(row._id)}>
                                <i className="fas fa-trash" /> Delete
                              </button>
                              {row.isActive ? (
                                <button type="button" className="notification-action-btn notification-action-btn--inactive" onClick={() => handleToggleActive(row._id)}>
                                  <i className="fas fa-times-circle" /> Inactive
                                </button>
                              ) : (
                                <button type="button" className="notification-action-btn notification-action-btn--active" onClick={() => handleToggleActive(row._id)}>
                                  <i className="fas fa-check-circle" /> Active
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </main>
  );
};

export default NotificationManagementPage;
